import { useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { INTENT_LIMITS, type OrderSideValue } from '@apna/shared'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { tickSocket } from '@/feed/tickSocket'
import { useTickLtps } from '@/feed/useTick'
import { useLeaveGuard } from '@/hooks/useLeaveGuard'
import { cn } from '@/lib/utils'
import { expiryLabel } from '@/lib/fmt'
import { limitTicketOrder, premiumAdvice, type ChainLimitTicket } from './chainLimitOrder'
import { placeOrder } from './placeOrder'

export function LimitOrderTicket({ ticket, onClose, onPlaced }: {
  ticket: ChainLimitTicket | null; onClose: () => void; onPlaced: () => void
}) {
  return ticket ? <Ticket key={ticket.requestId} ticket={ticket} onClose={onClose} onPlaced={onPlaced} /> : null
}

function Ticket({ ticket, onClose, onPlaced }: { ticket: ChainLimitTicket; onClose: () => void; onPlaced: () => void }) {
  const [side, setSide] = useState<OrderSideValue>('BUY')
  const [price, setPrice] = useState(ticket.quote > 0 ? ticket.quote.toFixed(2) : '')
  /*
   * THE SIZE CONTROL ANSWERS THIS, NOT A HARD-CODED 1.
   *
   * The ticket seeded `lots` from the screen's lot box, which in ₹ mode is
   * whatever the box happens to read while the real size lives in the
   * budget — so a ₹25,00,000 basket opened the ticket at one lot. Reported
   * 2026-10-27.
   *
   * Re-asked per SIDE because ₹ mode sizes a sell on the margin it blocks
   * and a buy on the premium it costs, and the side is chosen here.
   */
  const sized = useMemo(() => ticket.lotsFor(side), [ticket, side])
  /** Null until the trader types: the box follows the size control till then. */
  const [typedLots, setTypedLots] = useState<string | null>(null)
  const lots = typedLots ?? (sized.ok ? String(sized.lots) : '')
  const setLots = setTypedLots
  const [sl, setSl] = useState('')
  const [target, setTarget] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const busy = useRef(false)
  const combined = ticket.legs.length === 2

  /*
   * THE LIVE PREMIUM, NOT THE ONE CAPTURED WHEN THE TICKET OPENED.
   *
   * `useTickLtps` is ONE subscription whatever the leg count — the arity is
   * fixed so a one-leg and a two-leg ticket render the same number of hooks —
   * and it leaves a token ABSENT rather than zero when nothing has marked it.
   * That distinction is the whole of the rule below: an unmarked leg means we
   * have no combined premium to judge against, not a combined premium of zero.
   *
   * The chain already holds these tokens, so this costs no extra subscription
   * at the broker; it refcounts onto the ones the ladder behind the dialog is
   * already reading.
   */
  const ltps = useTickLtps(ticket.legs.map((l) => l.token))
  const livePremium = ticket.legs.every((l) => ltps.has(l.token))
    ? ticket.legs.reduce((sum, l) => sum + ltps.get(l.token)!, 0)
    : null
  const advice = useMemo(
    () => premiumAdvice({
      side,
      limitPrice: price.trim() ? Number(price) : null,
      stopLoss: sl.trim() ? Number(sl) : null,
      target: target.trim() ? Number(target) : null,
      ltp: livePremium,
      combined,
    }),
    [side, price, sl, target, livePremium, combined],
  )
  const blocking = advice.filter((a) => a.level === 'ERROR')

  /*
   * A half-typed ticket exists nowhere but this tab. `saving` is the stronger
   * case — the send is in the air — but a price someone has worked out and
   * entered is worth one dialog too, and the ticket is open for seconds, so
   * the listener it attaches is nowhere near the tab's whole lifetime.
   */
  useLeaveGuard(
    'limit-ticket',
    saving || price.trim() !== '' || sl.trim() !== '' || target.trim() !== '',
    saving ? 'A limit order is being sent' : 'A limit order ticket is open and has been typed into',
  )
  const context = tickSocket.getMarketContext()
  const mode = ticket.sessionId ? 'SIMULATION' : ticket.connection.mode
  const field = 'num h-8 w-full rounded-md border border-border bg-background px-2.5 text-[12.5px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 disabled:opacity-50'

  const submit = async () => {
    if (busy.current || uncertain) return
    if (context.sessionId !== ticket.sessionId) { setError('The market session changed. Close this ticket and open a new one.'); return }
    const result = limitTicketOrder(ticket, side, Number(lots), { limitPrice: Number(price), stopLoss: sl.trim() ? Number(sl) : null, target: target.trim() ? Number(target) : null })
    if (!result.ok) { setError(result.reason); return }
    if (blocking.length > 0) { setError(blocking[0]!.message); return }
    busy.current = true; setSaving(true); setError(null)
    try {
      const placed = await placeOrder(result.order, ticket.connection)
      if (placed.ok) {
        // NO TOAST ON SUCCESS. The drawer the caller opens IS the answer, and
        // an accepted order is not yet a filled one; only a refusal speaks.
        onClose(); onPlaced()
      } else {
        setError(placed.reason)
        // Keep an unknown result from becoming a second, edited order.
        setUncertain(/no acknowledgement|no answer|MAY have been recorded/i.test(placed.reason ?? ''))
      }
    } finally { busy.current = false; setSaving(false) }
  }

  return <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose() }}>
    <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[320px]" showCloseButton={!saving}
      onEscapeKeyDown={(e) => { if (saving) e.preventDefault() }} onPointerDownOutside={(e) => { if (saving) e.preventDefault() }}>
      {/*
        ───────────────────────────────────────────────────────────────────────────
        THE CONTRACT IS THE HEADING; EVERYTHING ELSE IS METADATA.

        This ran the underlying, the expiry, the strike and the side into one
        unbroken sentence under a generic "Limit order" title — "NIFTY 27 OCT
        22,100 CE + PE" — so the one thing a trader has to verify before
        committing money was the hardest part to read.

        Now the strike and side are the line the eye lands on, the underlying
        sits in front of it in a lighter weight, and the expiry joins the
        account and the mode on a single muted row beneath. "Combined premium
        per unit" is gone from here because the price field below already
        says "Combined limit (₹)" — the same fact, next to the input it
        governs.
        ───────────────────────────────────────────────────────────────────────────
      */}
      <header className="border-b border-border px-3.5 py-2.5">
        <DialogTitle className="sr-only">Limit order</DialogTitle>
        <DialogDescription className="sr-only">Set an entry premium and optional app-managed stop loss and target.</DialogDescription>
        <div className="pr-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Limit order</div>
        <div className="mt-0.5 flex items-baseline gap-1.5 text-[13.5px] leading-tight">
          <span className="font-medium text-muted-foreground">{ticket.underlying}</span>
          <span className="num font-semibold tracking-tight">{ticket.strike.toLocaleString('en-IN')}</span>
          <span className="font-semibold">{combined ? 'CE + PE' : ticket.legs[0]!.optionType}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          <span>{expiryLabel(ticket.expiry)}</span>
          <span aria-hidden="true">·</span>
          <span>{ticket.connection.label}</span>
          <span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-foreground/70">{mode}</span>
        </div>
      </header>
      <form onSubmit={(e) => { e.preventDefault(); void submit() }} className="p-3.5">
        <fieldset disabled={saving || uncertain} className="space-y-4">
          <div className="grid grid-cols-2 gap-1 rounded-md border border-border bg-secondary/50 p-1" role="group" aria-label="Order side">
            {(['BUY', 'SELL'] as const).map((value) => <button key={value} type="button" aria-pressed={side === value} onClick={() => setSide(value)}
              className={cn('rounded py-1 text-[11px] font-semibold transition-colors', side === value ? value === 'BUY' ? 'bg-success/10 text-success shadow-sm' : 'bg-destructive/10 text-destructive shadow-sm' : 'text-muted-foreground')}>
              {value === 'BUY' ? 'Buy' : 'Sell'}{combined ? ' both' : ''}
            </button>)}
          </div>
          {/*
            LOTS FIRST, THEN THE PRICE. The two are read in that order — how
            many, then at what — and the size is also the field a trader is
            most likely to leave alone, so it sits where the eye starts and
            the price gets the focus and the wider half.
          */}
          <div className="grid grid-cols-[1fr_1.4fr] gap-2.5">
            <label className="grid gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Lots
              <input aria-label="Lots" className={field} type="number" min={1} max={INTENT_LIMITS.maxLots} step={1} required value={lots} onChange={(e) => setLots(e.target.value)} />
            </label>
            <label className="grid gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{combined ? 'Combined limit (₹)' : 'Limit price (₹)'}
              <input autoFocus aria-label={combined ? 'Combined limit price' : 'Limit price'} className={cn(field, 'font-semibold text-foreground')} type="number" min={ticket.tickSize} step={ticket.tickSize} required value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
          </div>
          <div className="flex justify-between gap-3 text-[10px] text-muted-foreground">
            {/*
              THE NUMBER THE RULES ARE QUOTING. It used to read "LTP at open"
              forever, which was defensible while nothing else on the dialog
              had an opinion — but the advice below judges against the LIVE
              premium and prints it, so a frozen number beside it would be a
              second, contradicting answer. Falls back to the opening quote
              only while a leg is still unmarked, and says which it is.
            */}
            <span className="num">{livePremium !== null
              ? `${combined ? 'Combined' : 'LTP'} ₹${livePremium.toFixed(2)}`
              : ticket.quote > 0 ? `₹${ticket.quote.toFixed(2)} at open` : 'Waiting for quotes'}</span>
            {/* Why the box reads what it reads, when the screen sized it. */}
            {!sized.ok && typedLots === null ? (
              <span className="text-warning">{sized.why}</span>
            ) : (
              <span className="num">{Number(lots) * ticket.lotSize || 0} qty{combined ? ' per leg' : ''}</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2.5 border-t border-border pt-3">
            <label className="grid gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Stop loss (₹) <span className="sr-only">optional</span>
              <input aria-label="Stop loss price" className={cn(field, 'text-destructive')} type="number" min={ticket.tickSize} step={ticket.tickSize} placeholder="Optional" value={sl} onChange={(e) => setSl(e.target.value)} />
            </label>
            <label className="grid gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Target (₹) <span className="sr-only">optional</span>
              <input aria-label="Target price" className={cn(field, 'text-success')} type="number" min={ticket.tickSize} step={ticket.tickSize} placeholder="Optional" value={target} onChange={(e) => setTarget(e.target.value)} />
            </label>
          </div>
        </fieldset>
        {/*
          SAID, NEVER ENFORCED BY DISABLING. The button stays live whatever
          this reads: a marketable limit is a legitimate order that traders
          place deliberately to cross the spread, and a trader who means it
          should not have to work out what greyed the button out. The ERROR
          rows refuse the send from `submit` instead, which puts the reason
          in the same sentence as the refusal.
        */}
        {advice.length > 0 && <ul className="mt-3 grid gap-1.5">
          {advice.map((a) => <li key={a.code} role={a.level === 'ERROR' ? 'alert' : 'status'}
            className={cn('rounded border p-2 text-[11px]', a.level === 'ERROR'
              ? 'border-destructive/20 bg-destructive/5 text-destructive'
              : 'border-warning/25 bg-warning/5 text-warning')}>{a.message}</li>)}
        </ul>}
        {error && <div role="alert" className="mt-3 rounded border border-destructive/20 bg-destructive/5 p-2 text-[11px] text-destructive">{error}</div>}
        <footer className="mt-4 flex items-center justify-end gap-2">
          <button type="button" disabled={saving} onClick={onClose} className="rounded-md border border-border px-2.5 py-1.5 text-[11.5px] font-medium disabled:opacity-40">Cancel</button>
          {uncertain ? <button type="button" onClick={() => { onClose(); onPlaced() }} className="rounded-md bg-primary px-2.5 py-1.5 text-[11.5px] font-semibold text-primary-foreground">Check Orders</button> :
            <button type="submit" disabled={saving} className={cn('inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11.5px] font-semibold text-white disabled:opacity-50', side === 'BUY' ? 'bg-success' : 'bg-destructive')}>
              {saving && <Loader2 size={13} className="animate-spin" />}{saving ? 'Placing…' : `${side === 'BUY' ? 'Buy' : 'Sell'} limit${combined ? ' · both legs' : ''}`}
            </button>}
        </footer>
      </form>
    </DialogContent>
  </Dialog>
}
